/**
 * Directory store — S-DIR (RW-01 S1, S3, S4; ADR-005 v1.6 §3.1).
 *
 * One view serves both modes of the entry screen:
 *  - empty query  → the server-paged browse (first page, then "Load more");
 *  - typed query  → the suggestions (first page only, at most
 *    50 (SUGGESTION_LIMIT, PD-43), truncated when the server says `hasMore`).
 *
 * What this store never does (operator steering, PD-33, R1/R2):
 *  - no timer, no interval, no polling, no focus/visibility re-fetch — a read
 *    happens only because the user typed, pressed Load more or pressed Retry;
 *  - no read beyond the TPIN directory call in the api layer — nothing
 *    estate-wide before a TPIN is selected;
 *  - no debounce: a new keystroke aborts the request in flight, and an answer
 *    whose request was aborted (superseded or cancelled) is discarded (R10), so
 *    a superseded answer can never read as current.
 */

import { create } from 'zustand'
import { fetchDirectoryAdvance, fetchDirectoryFirstPage, type DirectoryQuery } from '@api/tpinApi'
import { SUGGESTION_LIMIT } from '@domain/tpinInput'
import type { TpinDirectoryPage } from '@domain/tpin'

export interface DirectoryList {
  readonly tins: readonly string[]
  readonly nextCursor: string | null
  readonly hasMore: boolean
}

/** State of the "Load more" request, separate from the list it extends. */
export type MoreState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }

export type DirectoryView =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly list: DirectoryList; readonly more: MoreState }
  | { readonly kind: 'error'; readonly message: string }

interface DirectoryState {
  /** Digits typed so far; '' means the browse. */
  readonly query: string
  readonly view: DirectoryView
  /** Set the query and read its first page (cancels whatever was in flight). */
  setQuery: (query: string) => void
  /** Read the next browse page, or retry a failed one. */
  loadMore: () => void
  /** Re-read the first page for the current query (the Retry action). */
  retry: () => void
  /** Cancel whatever is in flight; later answers are discarded. */
  cancel: () => void
}

const IDLE_MORE: MoreState = { kind: 'idle' }

let controller: AbortController | null = null

/** Supersede the request in flight and open a new one; an aborted signal marks a stale answer. */
function begin(): AbortSignal {
  controller?.abort()
  controller = new AbortController()
  return controller.signal
}

function queryParams(query: string): DirectoryQuery {
  return query === '' ? {} : { q: query, limit: SUGGESTION_LIMIT }
}

function failure(error: unknown): string {
  const detail = error instanceof Error ? error.message : 'unknown error'
  return `Couldn't reach the TPIN directory — ${detail}`
}

function listOf(page: TpinDirectoryPage): DirectoryList {
  return {
    tins: page.customers.map((customer) => customer.tin),
    nextCursor: page.nextCursor,
    hasMore: page.hasMore,
  }
}

export const useDirectoryStore = create<DirectoryState>((set, get) => {
  async function loadFirst(query: string): Promise<void> {
    const signal = begin()
    set({ query, view: { kind: 'loading' } })
    try {
      const page = await fetchDirectoryFirstPage(queryParams(query), signal)
      if (signal.aborted) return
      set({ view: { kind: 'ready', list: listOf(page), more: IDLE_MORE } })
    } catch (error) {
      if (signal.aborted) return
      set({ view: { kind: 'error', message: failure(error) } })
    }
  }

  return {
    query: '',
    view: { kind: 'loading' },

    setQuery: (query) => {
      void loadFirst(query)
    },

    loadMore: () => {
      const { view, query } = get()
      if (view.kind !== 'ready' || view.more.kind === 'loading' || view.list.nextCursor === null) return
      const cursor = view.list.nextCursor
      const signal = begin()
      set({ view: { ...view, more: { kind: 'loading' } } })
      void (async () => {
        try {
          const outcome = await fetchDirectoryAdvance(cursor, queryParams(query), signal)
          if (signal.aborted) return
          if (outcome.kind === 'past-end') {
            // Published §15: a 400 on an advanced cursor is the end of the list.
            set({ view: { kind: 'ready', list: { ...view.list, nextCursor: null, hasMore: false }, more: IDLE_MORE } })
            return
          }
          const next = listOf(outcome.page)
          set({
            view: {
              kind: 'ready',
              list: { tins: [...view.list.tins, ...next.tins], nextCursor: next.nextCursor, hasMore: next.hasMore },
              more: IDLE_MORE,
            },
          })
        } catch (error) {
          if (signal.aborted) return
          set({ view: { ...view, more: { kind: 'error', message: failure(error) } } })
        }
      })()
    },

    retry: () => {
      void loadFirst(get().query)
    },

    cancel: () => {
      controller?.abort()
    },
  }
})
